/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Handler hooks for saved views operations
 */

import { logger } from "@/lib/utils/logger";
import type { NormalizedSavedView } from "@/types/saved-views";
import { useMemo } from "react";
import type { SavedViewType } from "./types";

interface UseSavedViewsHandlersOptions<TFilters> {
  allViews: NormalizedSavedView<TFilters>[];
  selectedViewIdValue: string;
  selectedView: NormalizedSavedView<TFilters> | undefined;
  currentFilters: TFilters | undefined;
  defaultFilters: TFilters | undefined;
  viewType: SavedViewType;
  onApplyView?: (filters: TFilters) => void;
  applyingViewRef: React.MutableRefObject<boolean>;
  lastAppliedViewIdRef: React.MutableRefObject<string | null>;
  setSelectedViewId: (viewId: string | undefined) => void;
  createView: (input: {
    name: string;
    filters: Record<string, unknown>;
    isOrgPreset?: boolean;
  }) => Promise<{ view_id: string }>;
  updateView: (
    viewId: string,
    input: {
      name?: string;
      filters?: Record<string, unknown>;
      isOrgPreset?: boolean;
    },
  ) => Promise<{ view_id: string }>;
  deleteView: (viewId: string) => Promise<string>;
}

/**
 * Hook for saved views handlers (apply, save, delete)
 */
export function useSavedViewsHandlers<
  TFilters extends Record<string, unknown>,
>({
  allViews,
  selectedViewIdValue,
  selectedView,
  currentFilters,
  defaultFilters,
  viewType,
  onApplyView,
  applyingViewRef,
  lastAppliedViewIdRef,
  setSelectedViewId,
  createView,
  updateView,
  deleteView,
}: UseSavedViewsHandlersOptions<TFilters>) {
  // Check if current filters differ from selected view (dirty state)
  const isDirty = useMemo(() => {
    if (!currentFilters) {
      return false;
    }

    if (!selectedView) {
      // If no view selected, compare against defaultFilters
      // If defaultFilters not provided, consider dirty if we have any active filters
      if (defaultFilters) {
        // Deep equality check against defaultFilters
        return (
          JSON.stringify(defaultFilters) !== JSON.stringify(currentFilters)
        );
      }
      // Fallback: check if we have any active filters
      return Object.values(currentFilters).some((value) => {
        if (typeof value === "string") {
          return value !== "all" && value.trim().length > 0;
        }
        return !!value;
      });
    }

    const viewFilters = selectedView.filters as TFilters;

    // Deep equality check (simplified - assumes filters are plain objects)
    return JSON.stringify(viewFilters) !== JSON.stringify(currentFilters);
  }, [selectedView, currentFilters, defaultFilters]);

  // Handler to apply a saved view
  const handleApplyView = (viewId: string | null) => {
    if (applyingViewRef.current) return;

    // Update component state (not URL - viewId is user preference, not shareable)
    setSelectedViewId(viewId ?? undefined);

    if (!viewId) {
      // If clearing view, reset the last applied viewId and call onApplyView with default/reset filters
      lastAppliedViewIdRef.current = null;
      if (onApplyView) {
        onApplyView(defaultFilters ?? ({} as TFilters));
      }
      return;
    }

    const view = allViews.find((v) => v.id === viewId);
    if (!view || !onApplyView) return;

    applyingViewRef.current = true;
    lastAppliedViewIdRef.current = viewId;
    try {
      const viewFilters = view.filters as TFilters;
      onApplyView(viewFilters);
    } finally {
      // Reset after a brief delay to allow state updates to complete
      setTimeout(() => {
        applyingViewRef.current = false;
      }, 100);
    }
  };

  // Handler to save current view
  // Now accepts an optional name parameter - if not provided, will use window.prompt as fallback
  // Components should use SaveViewDialog instead of relying on this fallback
  const handleSaveView = async (name?: string) => {
    if (typeof window === "undefined" || !currentFilters) return;

    // Prompt for name if not provided (fallback for components that don't use SaveViewDialog)
    let viewName = name;
    if (!viewName) {
      // Only use window.prompt as a fallback - prefer SaveViewDialog in components
      if (typeof window.prompt === "function") {
        const input = window.prompt(
          "Name this view",
          selectedView?.name ?? `New ${viewType} view`,
        );
        if (!input) return;
        viewName = input.trim();
        if (!viewName) return;
      } else {
        logger.warn(
          "[useUnifiedSavedViews] handleSaveView called without name and window.prompt not available",
          { viewType },
        );
        return;
      }
    }

    try {
      if (selectedViewIdValue && selectedView) {
        // Update existing view
        await updateView(selectedViewIdValue, {
          name: viewName,
          filters: currentFilters as Record<string, unknown>,
        });
      } else {
        // Create new view
        const newView = await createView({
          name: viewName,
          filters: currentFilters as Record<string, unknown>,
          isOrgPreset: false,
        });

        // Update component state with new view ID (not URL - prevents render loops)
        // The view filters already match currentFilters, so we don't need to apply it again
        setSelectedViewId(newView.view_id);
        lastAppliedViewIdRef.current = newView.view_id;
      }
    } catch (error) {
      logger.error("Failed to save view", { error, viewType });
      // Error toast is handled by the mutation
      throw error; // Re-throw so SaveViewDialog can handle it
    }
  };

  // Handler to delete current view
  const handleDeleteView = async () => {
    if (!selectedViewIdValue || !selectedView) return;

    try {
      await deleteView(selectedViewIdValue);

      // Clear component state (not URL - prevents render loops)
      setSelectedViewId(undefined);
      lastAppliedViewIdRef.current = null;

      // Clear filters by applying default/reset filters
      if (onApplyView) {
        onApplyView(defaultFilters ?? ({} as TFilters));
      }
    } catch (error) {
      logger.error("Failed to delete view", { error, viewType });
      // Error toast is handled by the mutation
    }
  };

  return {
    isDirty,
    handleApplyView,
    handleSaveView,
    handleDeleteView,
  };
}
